import React from 'react';
import { Sparkles, Lock, ShieldCheck, Mail } from 'lucide-react';
import { useUsage } from '../context/UsageContext';
import { useFreemium } from '../context/FreemiumContext';
import { cn } from '../lib/utils';

/**
 * TrialBanner — banner sobrio del período de exploración (7 días).
 *
 * Estados:
 *   - admin / plan activo / unlimited_access / con créditos IA restantes: null (no se muestra).
 *   - trial activo (día N de 7): banner azul/slate con barra de progreso.
 *   - trial expirado + sin créditos IA + sin plan: banner rojo con candado + mensaje + CTA.
 */
export default function TrialBanner() {
    const {
        isAdmin,
        planActivo,
        trial,
        trialExpirado,
        trialDiasRestantes,
        trialPorcentaje,
        trialFechaFinHumano,
    } = useUsage();
    const { unlimitedAccess, freeIaCredits, status } = useFreemium();
    const overrideActive = !!status?.global_access_override_active;
    const grantActive = !!status?.user_access_grant_active;

    if (isAdmin || planActivo || unlimitedAccess || overrideActive || grantActive) return null;
    // Feb 2026: si el usuario aún tiene créditos IA (o su saldo no fue inicializado),
    // el trial expirado ya no aplica; el nuevo modelo Freemium unificado los cubre.
    if (freeIaCredits === null || freeIaCredits === undefined || freeIaCredits > 0) {
        // Sólo ocultar el estado "expirado". Si el trial está VIGENTE seguimos mostrándolo.
        if (trialExpirado) return null;
    }
    if (!trial) return null;

    const fechaFin = trialFechaFinHumano || '8 de agosto de 2026';

    const abrirSoporteActivar = () =>
        window.dispatchEvent(
            new CustomEvent('open-soporte', {
                detail: {
                    asunto: 'Activar plan Portafolio Digital Docente',
                    mensaje: 'Hola, me gustaría activar mi plan para continuar construyendo mi Portafolio.',
                },
            })
        );

    if (trialExpirado) {
        return (
            <div
                className="rounded-2xl border-2 border-rose-200 bg-gradient-to-br from-rose-50 via-white to-amber-50/40 p-5 sm:p-6 shadow-sm"
                data-testid="trial-expired-banner"
                role="status"
            >
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-100 text-rose-700">
                        <Lock className="h-5 w-5" />
                    </div>
                    <div className="flex-1 space-y-2">
                        <p className="font-heading text-base font-bold text-slate-900">
                            Tu período de exploración finalizó
                        </p>
                        <p className="text-sm text-slate-700 leading-relaxed">
                            Hemos conservado <strong>todo tu trabajo, avances, evidencias y estrategia</strong> para que puedas
                            continuar exactamente donde quedaste. Activa tu plan para seguir construyendo
                            tu Portafolio con acompañamiento profesional y orientación estratégica.
                        </p>
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                            <button
                                type="button"
                                onClick={abrirSoporteActivar}
                                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 transition-colors"
                                data-testid="trial-cta-activar"
                            >
                                <Mail className="h-4 w-4" />
                                Continuar mi Portafolio
                            </button>
                            <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                                <ShieldCheck className="h-3.5 w-3.5" />
                                Tu avance está guardado
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Acceso libre activo (todos los usuarios comparten la misma fecha de fin)
    const urgente = trialDiasRestantes <= 7;
    const tone = urgente
        ? {
              wrap: 'border-amber-200 bg-gradient-to-br from-amber-50 via-white to-rose-50/30',
              iconBg: 'bg-amber-100 text-amber-700',
              bar: 'bg-gradient-to-r from-amber-500 to-rose-500',
              chipBg: 'bg-amber-900 text-white',
          }
        : {
              wrap: 'border-slate-200 bg-gradient-to-br from-slate-50 via-white to-blue-50/30',
              iconBg: 'bg-slate-900 text-white',
              bar: 'bg-gradient-to-r from-slate-700 to-blue-700',
              chipBg: 'bg-slate-900 text-white',
          };

    return (
        <div
            className={cn(
                'rounded-2xl border-2 p-4 sm:p-5 shadow-sm',
                tone.wrap
            )}
            data-testid="trial-active-banner"
            role="status"
        >
            <div className="flex items-start gap-3">
                <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', tone.iconBg)}>
                    <Sparkles className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <p className="font-heading text-sm font-bold text-slate-900" data-testid="trial-active-title">
                            Acceso libre hasta el {fechaFin}
                        </p>
                        <span
                            className={cn(
                                'inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                                tone.chipBg
                            )}
                            data-testid="trial-day-chip"
                        >
                            {trialDiasRestantes === 1
                                ? 'Último día'
                                : `${trialDiasRestantes} días restantes`}
                        </span>
                    </div>
                    <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                        Tu avance se mantendrá <strong>guardado permanentemente</strong>. Al finalizar el acceso libre podrás
                        continuar activando tu plan — todo tu trabajo seguirá intacto.
                    </p>
                    {/* Barra de progreso de la campaña Early Access */}
                    <div
                        className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-200/70"
                        role="progressbar"
                        aria-valuenow={Math.round(trialPorcentaje)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        data-testid="trial-progress-bar"
                    >
                        <div
                            className={cn('h-full rounded-full transition-all duration-700 ease-out', tone.bar)}
                            style={{ width: `${Math.min(100, Math.max(2, trialPorcentaje))}%` }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
